/**
 * Search form for 患者リスト(施術師毎) — Patient List by Practitioner.
 * Validation rules live in @/schemas/visitPatientSearchSchema; errors are
 * surfaced via modal alert in the order RA2 blade JS uses (FROM → TO → massager)
 * so the UX matches legacy `alert(...)` popups.
 */

import React, { useCallback, useMemo } from 'react';
import { Controller, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';

import {
  AppButton,
  AppSelect,
  AppRadio,
  AppRadioGroup,
  AppInputNumber,
  AppFormLabel,
} from '@/components/atoms';
import { Search } from '@/components/atoms/AppIcon';
import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import { RA2NDatePicker } from '@/components/organisms';
import { SERVICE_TYPE_A2, MASSAGER_LABEL_A2, MASSAGER_LABEL_R2 } from '@/constants/serviceType';
import useAppModal from '@/hooks/useAppModal';
import { convGtJDate, dateValueToIso } from '@/utils/dateConverter';
import {
  visitPatientSearchSchema,
  type VisitPatientSearchFormValues,
} from '@/schemas/visitPatientSearchSchema';
import type { VisitPatientMassager, VisitPatientInsurerType } from '../types';

export type { VisitPatientSearchFormValues };

interface Props {
  onSearch: (values: VisitPatientSearchFormValues) => void;
  onDateFromChange?: (isoDate: string | null) => void;
  massagers: VisitPatientMassager[];
  insurerTypes: VisitPatientInsurerType[];
  serviceType: string;
  isLoading: boolean;
  /** Optional initial form values — typically hydrated from the URL on page reload. */
  initialValues?: VisitPatientSearchFormValues | null;
  /** Match-count badge value displayed next to the 検索 button (BD#10). */
  totalCount?: number;
}

const VisitPatientSearchForm: React.FC<Props> = ({
  onSearch,
  onDateFromChange,
  massagers,
  insurerTypes,
  serviceType,
  isLoading,
  initialValues,
  totalCount = 0,
}) => {
  const modal = useAppModal();
  const isA2 = serviceType === SERVICE_TYPE_A2;
  const massagerLabel = isA2 ? MASSAGER_LABEL_A2 : MASSAGER_LABEL_R2;

  const firstOfMonth = useMemo(() => convGtJDate(dayjs().startOf('month')), []);

  const defaultValues: VisitPatientSearchFormValues = useMemo(
    () =>
      initialValues ?? {
        visitDateFrom: firstOfMonth,
        visitDateTo: firstOfMonth,
        expirationDateFrom: '',
        expirationDateTo: '',
        insurerTypeId: null,
        ageFrom: null,
        ageTo: null,
        massagerRadio: '1',
        massagerId: null,
      },
    [initialValues, firstOfMonth]
  );

  const { control, handleSubmit, watch, setValue } = useTrimmedForm<VisitPatientSearchFormValues>({
    resolver: zodResolver(visitPatientSearchSchema),
    defaultValues,
  });

  const massagerRadio = watch('massagerRadio');

  const massagerOptions = useMemo(
    () => [
      { value: '', label: '' },
      ...massagers.map(m => ({
        value: m.id,
        label: `${m.serial_number ?? ''}:${m.name}`,
      })),
    ],
    [massagers]
  );

  const insurerTypeOptions = useMemo(
    () => [{ value: '', label: '' }, ...insurerTypes.map(t => ({ value: t.id, label: t.name }))],
    [insurerTypes]
  );

  // Spec §3.10: stop at the first error in fixed order. Modal preserves legacy
  // `alert()` UX from RA2 blade JS.
  const onInvalid = useCallback(
    (errors: FieldErrors<VisitPatientSearchFormValues>) => {
      const first =
        errors.visitDateFrom?.message ??
        errors.visitDateTo?.message ??
        errors.massagerId?.message?.replace('施術師', massagerLabel);
      if (first) modal.error({ title: first, okText: 'OK' });
    },
    [modal, massagerLabel]
  );

  const submit = handleSubmit(values => onSearch(values), onInvalid);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        submit();
      }
    },
    [submit]
  );

  return (
    <CollapsibleCard title={`患者リスト(${massagerLabel}毎)検索`}>
      <div onKeyDown={handleKeyDown}>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[140px_minmax(0,1fr)] lg:items-center">
          {/* Row 1: Visit date range */}
          <AppFormLabel label="訪問年月日" className="text-sm" />
          <div className="flex flex-col min-w-0 gap-2 sm:flex-row sm:items-center sm:gap-3">
            <Controller
              control={control}
              name="visitDateFrom"
              render={({ field }) => (
                <RA2NDatePicker
                  className="w-full sm:flex-1"
                  placeholder="元号00年00月00日"
                  value={field.value}
                  onChange={val => {
                    field.onChange(val);
                    onDateFromChange?.(dateValueToIso(val));
                  }}
                />
              )}
            />
            <span className="text-sm text-center sm:text-left">～</span>
            <Controller
              control={control}
              name="visitDateTo"
              render={({ field }) => (
                <RA2NDatePicker
                  className="w-full sm:flex-1"
                  placeholder="元号00年00月00日"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          {/* Row 2: Insurance expiry range */}
          <AppFormLabel label="保険証有効期限" className="text-sm" />
          <div className="flex flex-col min-w-0 gap-2 sm:flex-row sm:items-center sm:gap-3">
            <Controller
              control={control}
              name="expirationDateFrom"
              render={({ field }) => (
                <RA2NDatePicker
                  className="w-full sm:flex-1"
                  placeholder="元号00年00月00日"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <span className="text-sm text-center sm:text-left">～</span>
            <Controller
              control={control}
              name="expirationDateTo"
              render={({ field }) => (
                <RA2NDatePicker
                  className="w-full sm:flex-1"
                  placeholder="元号00年00月00日"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          {/* Row 3: Insurer type + Age */}
          <div className="grid grid-cols-1 gap-4 lg:col-span-2 lg:grid-cols-[140px_minmax(0,1fr)_80px_minmax(0,1fr)] lg:items-center">
            <AppFormLabel label="保険種別" className="text-sm" />
            <Controller
              control={control}
              name="insurerTypeId"
              render={({ field }) => (
                <AppSelect
                  className="w-full"
                  value={field.value ?? undefined}
                  onChange={val => field.onChange(val ? Number(val) : null)}
                  options={insurerTypeOptions}
                />
              )}
            />
            <AppFormLabel label="年齢" className="text-sm" />
            <div className="flex flex-wrap items-center min-w-0 gap-2">
              <Controller
                control={control}
                name="ageFrom"
                render={({ field }) => (
                  <AppInputNumber
                    className="w-20"
                    value={field.value ?? undefined}
                    onChange={val => field.onChange(val != null ? Number(val) : null)}
                    min={0}
                    max={999}
                    precision={0}
                    controls={false}
                  />
                )}
              />
              <span className="text-sm">歳</span>
              <span className="text-sm">～</span>
              <Controller
                control={control}
                name="ageTo"
                render={({ field }) => (
                  <AppInputNumber
                    className="w-20"
                    value={field.value ?? undefined}
                    onChange={val => field.onChange(val != null ? Number(val) : null)}
                    min={0}
                    max={999}
                    precision={0}
                    controls={false}
                  />
                )}
              />
              <span className="text-sm">歳</span>
            </div>
          </div>

          {/* Row 4: Practitioner radio + dropdown */}
          <AppFormLabel label={massagerLabel} className="text-sm" />
          <div className="flex flex-col min-w-0 gap-3 md:flex-row md:items-center md:gap-4">
            <Controller
              control={control}
              name="massagerRadio"
              render={({ field }) => (
                <AppRadioGroup
                  value={field.value}
                  onChange={e => {
                    const val = e.target.value as '1' | '2';
                    field.onChange(val);
                    if (val === '1') setValue('massagerId', null);
                  }}
                  className="flex flex-wrap gap-x-4 gap-y-1"
                >
                  <AppRadio value="1">指定なし</AppRadio>
                  <AppRadio value="2">指定あり</AppRadio>
                </AppRadioGroup>
              )}
            />
            <Controller
              control={control}
              name="massagerId"
              render={({ field }) => (
                <AppSelect
                  className="w-full md:flex-1"
                  value={field.value ?? undefined}
                  onChange={val => field.onChange(val ? Number(val) : null)}
                  options={massagerOptions}
                  disabled={massagerRadio === '1'}
                />
              )}
            />
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
          <div className="hidden sm:block" aria-hidden="true" />
          <AppButton
            type="primary"
            onClick={submit}
            loading={isLoading}
            className="w-full sm:w-auto"
          >
            <Search /> 検索
          </AppButton>
          <div className="flex justify-center sm:justify-end">
            <span className="px-3 py-1 text-sm font-medium leading-tight text-center text-black rounded-md bg-amber-400">
              該当件数 {totalCount}
            </span>
          </div>
        </div>
      </div>
    </CollapsibleCard>
  );
};

export default VisitPatientSearchForm;
